import { useState, useEffect, useRef } from 'react'
import { 
  PaperAirplaneIcon,
  PlusIcon,
  ChatBubbleLeftIcon,
  XMarkIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  DocumentArrowUpIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  BookOpenIcon,
  EyeIcon,
  CommandLineIcon
} from '@heroicons/react/24/outline'
import LoginPage from './components/LoginPage'
import SettingsModal from './SettingsModal'
import { GoogleUser } from './components/GoogleLogin'

interface Message {
  role: 'user' | 'assistant'
  content: string
  id: string
  timestamp: number
  mcpResults?: Array<{
    type: string
    service: string
    query?: string
    result?: any
    error?: string
    metadata?: any
  }>
}

interface ChatHistory {
  id: string
  title: string
  messages: Message[]
  timestamp: number
}

// 统一的Agent12 Logo组件
const Agent12Logo: React.FC<{ size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'; className?: string }> = ({ 
  size = 'md', 
  className = '' 
}) => {
  const sizeClasses = {
    xs: 'w-3 h-3',
    sm: 'w-4 h-4', 
    md: 'w-6 h-6',
    lg: 'w-10 h-10',
    xl: 'w-20 h-20'
  }
  
  const textSizes = {
    xs: 'text-xs',
    sm: 'text-xs',
    md: 'text-xs',
    lg: 'text-sm',
    xl: 'text-lg'
  }

  return (
    <div className={`${sizeClasses[size]} bg-gradient-to-r from-blue-600 to-purple-600 rounded-md flex items-center justify-center ${className}`}>
      <span className={`text-white font-bold ${textSizes[size]}`}>A12</span>
    </div>
  )
}

// WebSocket 聊天客户端类
class WebSocketChatClient {
  private ws: WebSocket | null = null
  private isConnected: boolean = false
  private isAuthenticated: boolean = false
  private responseHandlers: Map<string, (response: any) => void> = new Map()
  private textBuffer: string = ''
  private currentMessageId: string | null = null
  private onMessageCallback: ((content: string, isComplete: boolean, mcpResults?: any[]) => void) | null = null

  constructor(private url: string, private appId: string, private userId: string) {}

  async connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        this.ws = new WebSocket(this.url)
        
        this.ws.onopen = () => {
          console.log('🔗 WebSocket 连接已建立')
          this.isConnected = true
          resolve()
        }

        this.ws.onmessage = (event) => {
          try {
            const message = JSON.parse(event.data)
            this.handleMessage(message)
          } catch (error) {
            console.error('❌ 消息解析失败:', error)
          }
        }

        this.ws.onerror = (error) => {
          console.error('❌ WebSocket 错误:', error)
          reject(error)
        }

        this.ws.onclose = (event) => {
          console.log(`🔌 WebSocket 连接关闭: ${event.code}`)
          this.isConnected = false
          this.isAuthenticated = false
        }

        // 连接超时
        setTimeout(() => {
          if (!this.isConnected) {
            reject(new Error('WebSocket 连接超时'))
          }
        }, 10000)
      } catch (error) {
        reject(error)
      }
    })
  }

  private handleMessage(message: any): void {
    console.log('📥 收到消息:', message)

    // 处理心跳
    if (message.type === 'ping') {
      this.ws?.send(JSON.stringify({ type: 'pong', timestamp: Date.now() }))
      return
    }

    // 处理认证响应
    if (message.type === 'auth_success') {
      console.log('✅ 用户认证成功')
      this.isAuthenticated = true
      if (this.responseHandlers.has('auth')) {
        this.responseHandlers.get('auth')!(message)
        this.responseHandlers.delete('auth')
      }
      return
    }

    // 处理错误
    if (message.error) {
      console.error('❌ 服务器错误:', message.error)
      if (this.responseHandlers.has('auth')) {
        this.responseHandlers.get('auth')!(message)
        this.responseHandlers.delete('auth')
      }
      if (this.currentMessageId && this.responseHandlers.has(this.currentMessageId)) {
        this.responseHandlers.get(this.currentMessageId)!(message)
        this.responseHandlers.delete(this.currentMessageId)
      }
      return
    }

    // 处理流式内容 - 连续收到多条消息，每条都有content
    if (message.content !== undefined) {
      // 累积内容
      this.textBuffer += message.content
      
      // 检查是否是最后一条消息
      const isComplete = message.done === true
      
      if (isComplete) {
        console.log('✅ 流式响应完成')
        this.onMessageCallback?.(this.textBuffer, true, message.mcp_results)
        
        if (this.currentMessageId && this.responseHandlers.has(this.currentMessageId)) {
          this.responseHandlers.get(this.currentMessageId)!(message)
          this.responseHandlers.delete(this.currentMessageId)
        }
        
        // 清空缓冲区，准备下次对话
        this.textBuffer = ''
        this.currentMessageId = null
      } else {
        // 中间消息，继续累积内容
        this.onMessageCallback?.(this.textBuffer, false)
      }
      return
    }

    // 处理其他类型的响应
    if (this.currentMessageId && this.responseHandlers.has(this.currentMessageId)) {
      this.responseHandlers.get(this.currentMessageId)!(message)
      this.responseHandlers.delete(this.currentMessageId)
    }
  }

  async authenticate(): Promise<void> {
    if (!this.isConnected) {
      throw new Error('WebSocket 未连接')
    }

    return new Promise((resolve, reject) => {
      this.responseHandlers.set('auth', (response) => {
        if (response.type === 'auth_success') {
          resolve()
        } else {
          reject(new Error(response.error || '认证失败'))
        }
      })

      const authData = {
        type: "auth",
        app_id: this.appId,
        user_id: this.userId
      }

      this.ws?.send(JSON.stringify(authData))

      // 认证超时
      setTimeout(() => {
        if (this.responseHandlers.has('auth')) {
          this.responseHandlers.delete('auth')
          reject(new Error('认证超时'))
        }
      }, 10000)
    })
  }

  async sendMessage(
    content: string, 
    type: 'chat' | 'search' | 'translate' = 'chat',
    onMessage?: (content: string, isComplete: boolean, mcpResults?: any[]) => void
  ): Promise<void> {
    if (!this.isConnected || !this.isAuthenticated) {
      throw new Error('WebSocket 未连接或未认证')
    }

    this.textBuffer = ''
    this.onMessageCallback = onMessage || null
    const messageId = `msg_${Date.now()}`
    this.currentMessageId = messageId

    return new Promise((resolve, reject) => {
      this.responseHandlers.set(messageId, (response) => {
        if (response.error) {
          reject(new Error(response.error))
        } else {
          resolve()
        }
      })

      let message: any

      switch (type) {
        case 'search':
          message = {
            type: "search",
            query: content,
            maxResults: 5,
            extractContent: true
          }
          break
        case 'translate':
          message = {
            type: "translate",
            text: content,
            targetLanguage: "zh"
          }
          break
        default:
          message = {
            messages: [{ role: "user", content }]
          }
      }

      this.ws?.send(JSON.stringify(message))

      // 响应超时
      setTimeout(() => {
        if (this.responseHandlers.has(messageId)) {
          this.responseHandlers.delete(messageId)
          reject(new Error('响应超时'))
        }
      }, 60000)
    })
  }

  close(): void {
    if (this.ws) {
      this.ws.close()
    }
  }

  isReady(): boolean {
    return this.isConnected && this.isAuthenticated
  }
}

function App() {
  // 用户认证状态
  const [isLoggedIn, setIsLoggedIn] = useState(() => {
    return localStorage.getItem('is_logged_in') === 'true'
  })
  const [currentUser, setCurrentUser] = useState<GoogleUser | null>(() => {
    const saved = localStorage.getItem('user_info')
    return saved ? JSON.parse(saved) : null
  })

  // 聊天状态
  const [chatHistories, setChatHistories] = useState<ChatHistory[]>(() => {
    const saved = localStorage.getItem('chat_histories')
    return saved ? JSON.parse(saved) : []
  })
  const [currentChatId, setCurrentChatId] = useState<string | null>(null)
  const [input, setInput] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  
  // UI状态
  const [isSidebarOpen, setIsSidebarOpen] = useState(true)
  const [isRightPanelOpen, setIsRightPanelOpen] = useState(true)
  const [isSettingsOpen, setIsSettingsOpen] = useState(false)
  const [isProjectsExpanded, setIsProjectsExpanded] = useState(false)
  const [isHistoryExpanded, setIsHistoryExpanded] = useState(true)
  
  // 功能状态
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([])
  
  // 右侧面板菜单状态
  const [selectedMainMenu, setSelectedMainMenu] = useState<'Knowledge Base' | 'Mcp Mode'>('Mcp Mode')
  const [isMcpModeExpanded, setIsMcpModeExpanded] = useState(true)
  const [selectedMcpFunction, setSelectedMcpFunction] = useState<'Search' | 'Translation' | null>('Search')
  
  // Knowledge Base 相关状态
  const [isKnowledgeBaseExpanded, setIsKnowledgeBaseExpanded] = useState(false)
  const [knowledgeBases, setKnowledgeBases] = useState<Array<{id: string, name: string, docCount: number}>>([])
  const [isLoadingKnowledgeBases, setIsLoadingKnowledgeBases] = useState(false)
  const [selectedKnowledgeBase, setSelectedKnowledgeBase] = useState<string | null>(null)
  
  // 设置状态
  const [appId, setAppId] = useState(() => localStorage.getItem('app_id') || 'app_56d33ebeeb404187')
  const [userId, setUserId] = useState(() => localStorage.getItem('user_id') || '')
  
  // WebSocket 客户端状态
  const [wsClient, setWsClient] = useState<WebSocketChatClient | null>(null)
  const [isConnecting, setIsConnecting] = useState(false)
  const [connectionError, setConnectionError] = useState<string | null>(null)
  
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // 获取当前聊天
  const currentChat = currentChatId ? chatHistories.find(chat => chat.id === currentChatId) : null

  // 滚动到底部
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [currentChat?.messages])

  // 保存聊天历史到localStorage
  useEffect(() => {
    localStorage.setItem('chat_histories', JSON.stringify(chatHistories))
  }, [chatHistories])

  // 用户认证处理函数
  const handleLogin = (user: GoogleUser) => {
    localStorage.setItem('user_info', JSON.stringify(user))
    localStorage.setItem('is_logged_in', 'true')
    setCurrentUser(user)
    setIsLoggedIn(true)
    
    // 设置默认用户ID
    if (!userId) {
      const defaultUserId = user.id
      setUserId(defaultUserId)
      localStorage.setItem('user_id', defaultUserId)
    }
  }

  const handleLogout = () => {
    setCurrentUser(null)
    setIsLoggedIn(false)
    localStorage.removeItem('user_info')
    localStorage.removeItem('is_logged_in')
  }

  // 设置保存处理
  const handleSettingsSave = () => {
    localStorage.setItem('app_id', appId)
    localStorage.setItem('user_id', userId)
    setIsSettingsOpen(false)
    console.log('设置已保存:', { appId, userId })
  }

  // 文件上传处理
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || [])
    setUploadedFiles(prev => [...prev, ...files])
  }

  // 移除文件
  const removeFile = (index: number) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index))
  }

  // 创建新聊天
  const createNewChat = () => {
    const newChat: ChatHistory = {
      id: Date.now().toString(),
      title: '新对话',
      messages: [],
      timestamp: Date.now()
    }
    setChatHistories(prev => [newChat, ...prev])
    setCurrentChatId(newChat.id)
  }

  // 发送消息
  const sendMessage = async () => {
    if (!input.trim() && uploadedFiles.length === 0) return

    if (!wsClient?.isReady()) {
      console.error('WebSocket 未连接或未认证')
      if (!isConnecting) {
        await initializeWebSocket()
      }
      return
    }

    let chatId = currentChatId
    if (!chatId) {
      // 如果没有当前聊天，创建新的
      const newChat: ChatHistory = {
        id: Date.now().toString(),
        title: input.slice(0, 30) + (input.length > 30 ? '...' : ''),
        messages: [],
        timestamp: Date.now()
      }
      setChatHistories(prev => [newChat, ...prev])
      chatId = newChat.id
      setCurrentChatId(chatId)
    }

    const userMessage: Message = {
      role: 'user',
      content: input.trim(),
      id: Date.now().toString(),
      timestamp: Date.now()
    }

    // 添加用户消息
    setChatHistories(prev => prev.map(chat => 
      chat.id === chatId 
        ? { ...chat, messages: [...chat.messages, userMessage] }
        : chat
    ))

    const messageContent = input.trim()
    setInput('')
    setUploadedFiles([])
    setIsLoading(true)

    // 创建助手消息
    const assistantMessageId = (Date.now() + 1).toString()
    const assistantMessage: Message = {
      role: 'assistant',
      content: '',
      id: assistantMessageId,
      timestamp: Date.now()
    }

    // 添加空的助手消息，稍后更新内容
    setChatHistories(prev => prev.map(chat => 
      chat.id === chatId 
        ? { ...chat, messages: [...chat.messages, assistantMessage] }
        : chat
    ))

    try {
      // 确定消息类型
      let messageType: 'chat' | 'search' | 'translate' = 'chat'
      if (selectedMainMenu === 'Mcp Mode' && selectedMcpFunction) {
        messageType = selectedMcpFunction === 'Search' ? 'search' : 'translate'
      }

      // 发送消息并处理流式响应
      await wsClient.sendMessage(
        messageContent,
        messageType,
        (content: string, isComplete: boolean, mcpResults?: any[]) => {
          // 更新助手消息内容
          setChatHistories(prev => prev.map(chat => 
            chat.id === chatId 
              ? {
                  ...chat, 
                  messages: chat.messages.map(msg => 
                    msg.id === assistantMessageId 
                      ? { 
                          ...msg, 
                          content,
                          ...(isComplete && mcpResults ? { mcpResults } : {})
                        }
                      : msg
                  )
                }
              : chat
          ))

          if (isComplete) {
            setIsLoading(false)
          }
        }
      )
    } catch (error) {
      console.error('发送消息失败:', error)
      
      // 更新助手消息为错误信息
      setChatHistories(prev => prev.map(chat => 
        chat.id === chatId 
          ? {
              ...chat, 
              messages: chat.messages.map(msg => 
                msg.id === assistantMessageId 
                  ? { 
                      ...msg, 
                      content: `抱歉，发送消息失败：${error instanceof Error ? error.message : '未知错误'}`
                    }
                  : msg
              )
            }
          : chat
      ))
      
      setIsLoading(false)
    }
  }

  // 删除聊天
  const deleteChat = (chatId: string) => {
    setChatHistories(prev => prev.filter(chat => chat.id !== chatId))
    if (currentChatId === chatId) {
      setCurrentChatId(null)
    }
  }

  // 获取知识库列表
  const fetchKnowledgeBases = async () => {
    setIsLoadingKnowledgeBases(true)
    try {
      // TODO: 调用实际的后端接口
      // const response = await fetch('/api/knowledge-bases')
      // const data = await response.json()
      
      // 模拟API调用
      setTimeout(() => {
        const mockData = [
          { id: 'kb1', name: '技术文档库', docCount: 1234 },
          { id: 'kb2', name: '企业知识库', docCount: 567 },
          { id: 'kb3', name: '产品手册', docCount: 89 },
          { id: 'kb4', name: 'FAQ集合', docCount: 156 }
        ]
        setKnowledgeBases(mockData)
        setIsLoadingKnowledgeBases(false)
      }, 800)
    } catch (error) {
      console.error('获取知识库列表失败:', error)
      setIsLoadingKnowledgeBases(false)
    }
  }

  // 处理Knowledge Base菜单点击
  const handleKnowledgeBaseClick = () => {
    setSelectedMainMenu('Knowledge Base')
    if (!isKnowledgeBaseExpanded) {
      setIsKnowledgeBaseExpanded(true)
      if (knowledgeBases.length === 0) {
        fetchKnowledgeBases()
      }
    } else {
      setIsKnowledgeBaseExpanded(!isKnowledgeBaseExpanded)
    }
  }

  // 初始化WebSocket连接
  const initializeWebSocket = async () => {
    if (!appId || !userId) {
      console.warn('缺少必要的配置信息')
      return
    }

    if (wsClient?.isReady()) {
      return // 已经连接
    }

    setIsConnecting(true)
    setConnectionError(null)

    try {
      const client = new WebSocketChatClient('wss://wsapi.o3-app.com/ws', appId, userId)
      
      await client.connect()
      console.log('🔗 WebSocket 连接成功')
      
      await client.authenticate()
      console.log('✅ 用户认证成功')
      
      setWsClient(client)
      setConnectionError(null)
    } catch (error) {
      console.error('❌ WebSocket 初始化失败:', error)
      setConnectionError(error instanceof Error ? error.message : '连接失败')
    } finally {
      setIsConnecting(false)
    }
  }

  // 清理WebSocket连接
  const cleanupWebSocket = () => {
    if (wsClient) {
      wsClient.close()
      setWsClient(null)
    }
  }

  // 用户登录后初始化WebSocket
  useEffect(() => {
    if (isLoggedIn && appId && userId) {
      initializeWebSocket()
    }
    
    return cleanupWebSocket
  }, [isLoggedIn, appId, userId])

  // 组件卸载时清理连接
  useEffect(() => {
    return cleanupWebSocket
  }, [])

  // 手动重连WebSocket
  const reconnectWebSocket = async () => {
    console.log('🔄 手动触发重连...')
    
    // 先清理现有连接
    cleanupWebSocket()
    
    // 重新初始化连接
    await initializeWebSocket()
  }

  // 如果用户未登录，显示登录页面
  if (!isLoggedIn) {
    return <LoginPage onLogin={handleLogin} />
  }

  return (
    <div className="flex h-screen bg-gray-50">
      {/* 左侧边栏 */}
      <div className={`${isSidebarOpen ? 'w-48' : 'w-0'} bg-white border-r border-gray-200 transition-all duration-300 overflow-hidden flex flex-col`}>
        {/* 侧边栏头部 */}
        <div className="p-3 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <Agent12Logo size="md" />
            <span className="text-base font-bold">agent12</span>
          </div>
        </div>

        {/* 快捷操作 */}
        <div className="p-3 space-y-2">
          <button
            onClick={createNewChat}
            className="w-full py-2 px-3 bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center gap-2 transition-colors"
          >
            <ChatBubbleLeftIcon className="w-4 h-4" />
            <span className="text-xs">新对话</span>
          </button>
          
          <button className="w-full py-2 px-3 bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center gap-2 transition-colors">
            <BookOpenIcon className="w-4 h-4" />
            <span className="text-xs">知识花园</span>
          </button>
          
          <button className="w-full py-2 px-3 bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center gap-2 transition-colors justify-between">
            <div className="flex items-center gap-2">
              <Agent12Logo size="sm" />
              <span className="text-xs">发现更多</span>
            </div>
            <span className="text-xs bg-orange-500 text-white px-1.5 py-0.5 rounded text-xs">Popular</span>
          </button>
        </div>

        {/* Projects 部分 */}
        <div className="px-3">
          <button
            onClick={() => setIsProjectsExpanded(!isProjectsExpanded)}
            className="w-full flex items-center justify-between py-1.5 text-gray-600 hover:text-gray-900"
          >
            <span className="font-medium text-xs">Projects</span>
            <div className="flex items-center gap-1">
              <PlusIcon className="w-3 h-3" />
              {isProjectsExpanded ? <ChevronDownIcon className="w-3 h-3" /> : <ChevronRightIcon className="w-3 h-3" />}
            </div>
          </button>
        </div>

        {/* History 部分 */}
        <div className="px-3 mt-3">
          <button
            onClick={() => setIsHistoryExpanded(!isHistoryExpanded)}
            className="w-full flex items-center justify-between py-1.5 text-gray-600 hover:text-gray-900"
          >
            <span className="font-medium text-xs">History</span>
            {isHistoryExpanded ? <ChevronDownIcon className="w-3 h-3" /> : <ChevronRightIcon className="w-3 h-3" />}
          </button>
        </div>

        {/* 聊天历史 */}
        {isHistoryExpanded && (
          <div className="flex-1 overflow-y-auto px-3">
            {chatHistories.map((chat) => (
              <div
                key={chat.id}
                onClick={() => setCurrentChatId(chat.id)}
                className={`p-2 mx-0 my-1 rounded-lg cursor-pointer transition-colors group hover:bg-gray-100 ${
                  currentChatId === chat.id ? 'bg-blue-50 border-l-2 border-blue-500' : ''
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <Agent12Logo size="xs" className="flex-shrink-0 text-gray-400" />
                    <span className="text-xs truncate">{chat.title}</span>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      deleteChat(chat.id)
                    }}
                    className="opacity-0 group-hover:opacity-100 p-0.5 hover:bg-gray-200 rounded"
                  >
                    <XMarkIcon className="w-3 h-3" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* 用户信息 */}
        <div className="p-3 border-t border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {currentUser?.picture ? (
                <img 
                  src={currentUser.picture} 
                  alt={currentUser.name}
                  className="w-6 h-6 rounded-full"
                />
              ) : (
                <div className="w-6 h-6 bg-orange-500 rounded-full flex items-center justify-center">
                  <span className="text-white text-xs font-medium">
                    {currentUser?.name?.charAt(0) || 'U'}
                  </span>
                </div>
              )}
              <div>
                <div className="text-xs font-medium">{currentUser?.name || '用户'}</div>
                <div className="text-xs text-green-600">Professional</div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setIsSettingsOpen(true)}
                className="p-1.5 hover:bg-gray-100 rounded-lg"
              >
                <Cog6ToothIcon className="w-3 h-3" />
              </button>
              <button
                onClick={handleLogout}
                className="p-1.5 hover:bg-gray-100 rounded-lg text-red-500"
              >
                <ArrowRightOnRectangleIcon className="w-3 h-3" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 主聊天区域 */}
      <div className="flex-1 flex flex-col">
        {/* 顶部栏 */}
        <div className="h-14 bg-white border-b border-gray-200 flex items-center justify-between px-4">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              className="p-1.5 hover:bg-gray-100 rounded-lg"
            >
              <ChatBubbleLeftIcon className="w-4 h-4" />
            </button>
            <div>
              <h1 className="text-base font-semibold text-gray-900">
                {currentChat?.title || 'Afternoon spark'}
              </h1>
              {!currentChat && (
                <p className="text-xs text-gray-500">The world needs what only you can create</p>
              )}
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {/* WebSocket 连接状态 - 可点击重连 */}
            <button
              onClick={reconnectWebSocket}
              disabled={isConnecting}
              className="flex items-center gap-1.5 px-2 py-1 rounded-md text-xs hover:bg-gray-100 transition-colors disabled:cursor-not-allowed"
              title={
                isConnecting ? '连接中...' :
                wsClient?.isReady() ? '已连接 - 点击重连' :
                connectionError ? '连接失败 - 点击重连' :
                '未连接 - 点击连接'
              }
            >
              {isConnecting ? (
                <>
                  <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600"></div>
                  <span className="text-gray-600">连接中</span>
                </>
              ) : wsClient?.isReady() ? (
                <>
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span className="text-green-600">已连接</span>
                </>
              ) : connectionError ? (
                <>
                  <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                  <span className="text-red-600">连接失败</span>
                </>
              ) : (
                <>
                  <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                  <span className="text-gray-500">未连接</span>
                </>
              )}
            </button>
            
            <button
              onClick={() => setIsRightPanelOpen(!isRightPanelOpen)}
              className="p-1.5 hover:bg-gray-100 rounded-lg"
            >
              <EyeIcon className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* 消息区域 */}
        <div className="flex-1 overflow-y-auto p-6">
          {currentChat?.messages.length === 0 || !currentChat ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center max-w-2xl">
                <Agent12Logo size="xl" className="mx-auto mb-6 rounded-full" />
                <h2 className="text-2xl font-bold text-gray-900 mb-4">开始新的对话</h2>
                <p className="text-gray-600 mb-8">给 Neo 一个任务，让它在云端自主规划、调用工具、并多步骤为你执行完成</p>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {currentChat.messages.map((message) => (
                <div key={message.id}>
                  <div className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div
                      className={`max-w-3xl p-4 rounded-lg ${
                        message.role === 'user'
                          ? 'bg-blue-600 text-white'
                          : 'bg-white border border-gray-200 text-gray-900'
                      }`}
                    >
                      <div className="whitespace-pre-wrap break-words leading-relaxed">{message.content}</div>
                    </div>
                  </div>
                  
                  {/* MCP 结果展示 */}
                  {message.mcpResults && (
                    <div className="mt-4 space-y-2">
                      {message.mcpResults.map((result, index) => (
                        <div key={index} className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4">
                          <div className="flex items-center gap-3 mb-3">
                            <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                              <span className="text-white text-sm">
                                {result.type === 'web_search' ? '🔍' : '🌐'}
                              </span>
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-semibold text-blue-900">
                                  MCP {result.type === 'web_search' ? '搜索' : '翻译'}
                                </span>
                                <span className="text-xs bg-blue-200 text-blue-700 px-2 py-1 rounded-full">
                                  {result.service}
                                </span>
                              </div>
                              {result.metadata && (
                                <div className="text-xs text-blue-600 mt-1">
                                  {result.type === 'web_search' && (
                                    <>搜索引擎: {result.metadata.searchEngine} • 结果数: {result.metadata.resultsCount} • 耗时: {result.metadata.searchTime}</>
                                  )}
                                  {result.type === 'translation' && (
                                    <>检测语言: {result.metadata.detectedLanguage} • 目标语言: {result.metadata.targetLanguages.join(', ')} • 置信度: {(result.metadata.confidence * 100).toFixed(0)}%</>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="bg-white rounded-lg p-3 border border-blue-100">
                            <div className="text-sm text-gray-800 whitespace-pre-wrap break-words leading-relaxed">{result.result}</div>
                            {result.query && (
                              <div className="mt-2 text-xs text-gray-500">
                                查询: "{result.query}"
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              
              {isLoading && (
                <div className="flex justify-start">
                  <div className="bg-white border border-gray-200 text-gray-900 max-w-3xl p-4 rounded-lg">
                    <div className="flex items-center gap-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                      <span>AI正在思考中...</span>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {/* 文件上传显示 */}
        {uploadedFiles.length > 0 && (
          <div className="px-5 py-1.5 border-t border-gray-200 bg-gray-50">
            <div className="flex flex-wrap gap-2">
              {uploadedFiles.map((file, index) => (
                <div key={index} className="flex items-center gap-2 bg-white px-3 py-2 rounded-lg border">
                  <DocumentArrowUpIcon className="w-4 h-4 text-gray-500" />
                  <span className="text-sm text-gray-700">{file.name}</span>
                  <button
                    onClick={() => removeFile(index)}
                    className="text-gray-400 hover:text-red-500"
                  >
                    <XMarkIcon className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 输入区域 */}
        <div className="border-t border-gray-200 p-5 bg-white">
          <div className="flex gap-3">
            <div className="flex-1">
              <div className="relative">
                <textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault()
                      sendMessage()
                    }
                  }}
                  placeholder="给 Neo 一个任务，让它在云端自主规划、调用工具、并多步骤为你执行完成..."
                  className="w-full px-3 py-2.5 pr-16 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={1}
                  disabled={isLoading}
                />
                <div className="absolute right-2 bottom-2 flex items-center gap-1">
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="p-1.5 text-gray-400 hover:text-gray-600 rounded"
                  >
                    <DocumentArrowUpIcon className="w-4 h-4" />
                  </button>
                  <button
                    onClick={sendMessage}
                    disabled={(!input.trim() && uploadedFiles.length === 0) || isLoading}
                    className="p-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <PaperAirplaneIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 右侧面板 */}
      {isRightPanelOpen && (
        <div className="w-48 bg-white border-l border-gray-200 flex flex-col">
          {/* 一级菜单 */}
          <div className="border-b border-gray-200">
            {/* Knowledge Base */}
            <div className="p-3 border-b border-gray-100">
              <button
                onClick={handleKnowledgeBaseClick}
                className={`w-full flex items-center justify-between py-1.5 text-left ${
                  selectedMainMenu === 'Knowledge Base' ? 'text-blue-600 font-medium' : 'text-gray-700'
                }`}
              >
                <div className="flex items-center gap-2">
                  <BookOpenIcon className="w-4 h-4" />
                  <span className="text-xs">Knowledge Base</span>
                </div>
                {isKnowledgeBaseExpanded ? <ChevronDownIcon className="w-3 h-3" /> : <ChevronRightIcon className="w-3 h-3" />}
              </button>

              {/* 二级菜单 - Knowledge Base 列表 */}
              {isKnowledgeBaseExpanded && (
                <div className="mt-2 ml-6 space-y-1.5">
                  {isLoadingKnowledgeBases ? (
                    <div className="flex items-center gap-2 text-xs text-gray-500 py-2">
                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600"></div>
                      <span>加载知识库列表...</span>
                    </div>
                  ) : (
                    knowledgeBases.map((kb) => (
                      <button
                        key={kb.id}
                        onClick={() => setSelectedKnowledgeBase(
                          selectedKnowledgeBase === kb.id ? null : kb.id
                        )}
                        className={`w-full text-left px-2 py-1.5 rounded-md transition-colors ${
                          selectedKnowledgeBase === kb.id 
                            ? 'bg-blue-50 text-blue-700 border border-blue-200' 
                            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                        }`}
                      >
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs">📚</span>
                          <span className="text-xs font-medium">{kb.name}</span>
                          {selectedKnowledgeBase === kb.id && (
                            <span className="text-xs bg-green-100 text-green-700 px-1 py-0.5 rounded text-xs ml-auto">激活</span>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 mt-0.5">
                          包含{kb.docCount}个文档
                          {selectedKnowledgeBase === kb.id && ' • 点击取消'}
                        </p>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>

            {/* Mcp Mode */}
            <div className="p-3">
              <button
                onClick={() => {
                  setSelectedMainMenu('Mcp Mode')
                  setIsMcpModeExpanded(!isMcpModeExpanded)
                }}
                className={`w-full flex items-center justify-between py-1.5 text-left ${
                  selectedMainMenu === 'Mcp Mode' ? 'text-blue-600 font-medium' : 'text-gray-700'
                }`}
              >
                <div className="flex items-center gap-2">
                  <CommandLineIcon className="w-4 h-4" />
                  <div>
                    <span className="text-xs">Mcp Mode</span>
                    {selectedMainMenu === 'Mcp Mode' && (
                      <div className="text-xs text-gray-500 mt-0.5">
                        {selectedMcpFunction ? (
                          <>
                            当前: {selectedMcpFunction} • 
                            {selectedMcpFunction === 'Search' ? '智能搜索已激活' : '翻译功能已激活'}
                          </>
                        ) : (
                          '未选择功能'
                        )}
                      </div>
                    )}
                  </div>
                </div>
                {isMcpModeExpanded ? <ChevronDownIcon className="w-3 h-3" /> : <ChevronRightIcon className="w-3 h-3" />}
              </button>

              {/* 二级菜单 - Mcp Mode子选项 */}
              {isMcpModeExpanded && (
                <div className="mt-2 ml-6 space-y-1.5">
                  <button
                    onClick={() => setSelectedMcpFunction(
                      selectedMcpFunction === 'Search' ? null : 'Search'
                    )}
                    className={`w-full text-left px-2 py-1.5 rounded-md transition-colors ${
                      selectedMcpFunction === 'Search' 
                        ? 'bg-blue-50 text-blue-700 border border-blue-200' 
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                  >
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs">🔍</span>
                      <span className="text-xs font-medium">Search</span>
                      {selectedMcpFunction === 'Search' && (
                        <span className="text-xs bg-green-100 text-green-700 px-1 py-0.5 rounded text-xs ml-auto">激活</span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">
                      智能网络搜索 • 实时获取最新信息
                      {selectedMcpFunction === 'Search' && ' • 点击取消'}
                    </p>
                  </button>

                  <button
                    onClick={() => setSelectedMcpFunction(
                      selectedMcpFunction === 'Translation' ? null : 'Translation'
                    )}
                    className={`w-full text-left px-2 py-1.5 rounded-md transition-colors ${
                      selectedMcpFunction === 'Translation' 
                        ? 'bg-blue-50 text-blue-700 border border-blue-200' 
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                  >
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs">🌐</span>
                      <span className="text-xs font-medium">Translation</span>
                      {selectedMcpFunction === 'Translation' && (
                        <span className="text-xs bg-green-100 text-green-700 px-1 py-0.5 rounded text-xs ml-auto">激活</span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">
                      多语言翻译 • 支持100+种语言
                      {selectedMcpFunction === 'Translation' && ' • 点击取消'}
                    </p>
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* 内容区域 */}
          <div className="flex-1 overflow-y-auto p-3">
            {/* 内容区域现在为空，所有功能都集成在菜单中 */}
          </div>
        </div>
      )}

      {/* 设置模态框 */}
      {isSettingsOpen && (
        <SettingsModal
          isOpen={isSettingsOpen}
          onClose={() => setIsSettingsOpen(false)}
          appId={appId}
          userId={userId}
          onAppIdChange={setAppId}
          onUserIdChange={setUserId}
          onSave={handleSettingsSave}
        />
      )}
    </div>
  )
}

export default App 